import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Form, Upload, Typography, Progress } from 'antd';
import { FileAddOutlined } from '@ant-design/icons';
import { toast } from 'react-hot-toast';
import createNoteTemplate from '../../utils/note_templates/createNoteTemplate';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

const NoteTemplateNewModal = ({ 
  open, 
  onCancel, 
  onTemplateCreated 
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [isCreating, setIsCreating] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [progress, setProgress] = useState(0);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setIsCreating(false);
      setIsPolling(false);
      setProgress(0);
      form.resetFields();
    }
  }, [open, form]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsCreating(false);
      setIsPolling(false);
      setProgress(0);
    };
  }, []);

  // Progress bar timer effect
  useEffect(() => {
    let interval;
    if (isCreating || isPolling) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            return 100;
          }
          return prev + (100 / 30); // Complete in 30 seconds
        });
      }, 1000);
    } else {
      setProgress(0);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isCreating, isPolling]);

  const handleCancel = () => {
    // Don't allow closing while processing
    if (isCreating || isPolling) {
      return;
    }
    
    form.resetFields();
    setIsCreating(false);
    setIsPolling(false);
    setProgress(0);
    onCancel();
  };

  return (
    <Modal
      title={t('note_templates.create_title')}
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={600}
      closable={!isCreating && !isPolling}
      maskClosable={!isCreating && !isPolling}
    >
      {!isCreating && !isPolling && (
        <div style={{ fontSize: '13px', justifyContent: 'center', display: 'flex', marginBottom: 20, flexDirection: 'column' }}>
          <Text>{t('note_templates.create_subtitle')}</Text>
          <span>
            <Text style={{ fontWeight: 'bold' }}>{t('note_templates.create_subtitle_2')}</Text>
          </span>
        </div>
      )}
      
      <Form form={form} layout="vertical">
        <Form.Item
          label={null}
        >
          <Dragger
            accept=".txt,.pdf,.docx"
            disabled={isCreating || isPolling}
            beforeUpload={async (file) => {
              // Validate file size (max 5MB)
              const maxSize = 5 * 1024 * 1024;
              if (file.size > maxSize) {
                toast.error(t('note_templates.errors.file_too_large'));
                return false;
              }
              
              // Validate file type
              const allowedTypes = [
                'text/plain', 
                'application/pdf',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              ];
              const allowedExtensions = ['txt', 'pdf', 'docx'];
              const fileExtension = file.name.split('.').pop().toLowerCase();
              
              if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
                toast.error(t('note_templates.errors.unsupported_format'));
                return false;
              }
              
              // File is valid, automatically create template
              try {
                setIsCreating(true);
                const presenter = await createNoteTemplate(
                  t('note_templates.default_name'), // Use default name
                  null, // No content initially
                  file
                );
                
                if (presenter) {
                  // Template created, now we need to poll until it's ready
                  setIsCreating(false);
                  setIsPolling(true);
                  onTemplateCreated(presenter);
                  // Don't close modal - let it stay open in analyzing state while polling
                }
              } catch (error) {
                console.error('Error creating template:', error);
                toast.error(t('note_templates.errors.save'));
                setIsCreating(false);
              }
              
              return false; // Prevent auto upload
            }}
            showUploadList={false}
            multiple={false}
            style={{ 
              cursor: (isCreating || isPolling) ? 'default' : 'pointer',
              padding: '20px',
              opacity: (isCreating || isPolling) ? 0.6 : 1
            }}
          >
            {isCreating ? (
              <div style={{ textAlign: 'center' }}>
                <Progress 
                  type="circle" 
                  percent={Math.round(progress)} 
                  format={percent => `${percent}%`}
                  size={80}
                />
                <div style={{ marginTop: 16 }}>
                  <Paragraph style={{ margin: 0, textAlign: 'center', color: '#888', fontSize: 15 }}>
                    {progress >= 100 ? t('note_templates.almost_done') : t('note_templates.processing')}
                  </Paragraph>
                </div>
              </div>
            ) : isPolling ? (
              <div style={{ textAlign: 'center' }}>
                <Progress 
                  type="circle" 
                  percent={Math.round(progress)} 
                  format={percent => `${percent}%`}
                  size={80}
                />
                <div style={{ marginTop: 16 }}>
                  <Paragraph style={{ margin: 0, textAlign: 'center', color: '#888', fontSize: 15 }}>
                    {progress >= 100 ? t('note_templates.almost_done') : t('note_templates.analyzing')}
                  </Paragraph>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                  <FileAddOutlined style={{ fontSize: 25, color: '#1890ff', marginRight: 12 }} />
                  <Paragraph style={{ margin: 0, textAlign: 'left', color: '#888', fontSize: 15 }}>
                    {t('note_templates.drag_drop_hint')}
                  </Paragraph>
                </div>
              </>
            )}
          </Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NoteTemplateNewModal;
